/**
 * Les deux fonds du site, et ce qui doit changer avec eux.
 *
 * Gala des Étoiles n'a que deux fonds : l'ENCRE (bleu nuit) et la CRÈME. Une
 * section sur deux, en alternance, et l'or en accent sur les deux. Jusqu'ici,
 * chaque bloc écrivait ses couleurs en dur — quatorze fichiers redéclarent
 * `const ACCENT = '#C2A66B'` — si bien qu'un bloc ne pouvait pas changer de
 * fond : le texte y était écrit pour UN fond et devenait illisible sur l'autre
 * (encre sur encre).
 *
 * C'est devenu bloquant le 2026-10-06 : les sections de la soirée s'effaçant
 * après l'événement, la suite des fonds se retrouve avec deux blocs de même
 * couleur côte à côte (mesuré : « Un héritage d'exception » suivi de « Ce qui
 * nous anime », tous deux crème ; « Partenaires » suivi de « Révélation »,
 * tous deux encre). Corriger à la main ne tient pas : chaque correction
 * déplace la collision, et tout se redécale dès qu'une soirée revient.
 *
 * Un bloc demande donc désormais sa palette à cette fonction, et l'alternance
 * est calculée au rendu (`lib/composition/alternanceFonds.ts`).
 *
 * ⚠️ Les valeurs sont celles qui étaient déjà en place, à l'identique : un bloc
 * rendu sur SON fond d'origine doit être pixel pour pixel celui d'avant.
 */

export type Fond = 'dark' | 'light'

/** Bleu nuit de la DA. */
export const ENCRE = '#021734'
/** Crème de la DA. */
export const CREME = '#F7EFE1'
/** L'or : accent commun aux deux fonds, inchangé. */
export const OR = '#C2A66B'

export interface Palette {
  /** La couleur de fond du bloc. */
  fond: string
  /** Titres et textes pleins. */
  titre: string
  /**
   * Un texte courant, à l'opacité d'origine du bloc (0.85, 0.62…). On garde
   * l'opacité plutôt qu'une couleur figée : c'est elle qui porte la hiérarchie
   * du texte, et elle doit survivre au changement de fond.
   */
  texte: (opacite: number) => string
  /** L'or. */
  accent: string
  /** Vrai sur fond encre — pour les composants qui prennent déjà un drapeau. */
  sombre: boolean
}

export function palette(fond: Fond): Palette {
  const sombre = fond === 'dark'
  return {
    fond: sombre ? ENCRE : CREME,
    titre: sombre ? CREME : ENCRE,
    texte: (opacite: number) =>
      sombre ? `rgba(247,239,225,${opacite})` : `rgba(2,23,52,${opacite})`,
    accent: OR,
    sombre,
  }
}

/** L'autre fond. */
export function fondOppose(fond: Fond): Fond {
  return fond === 'dark' ? 'light' : 'dark'
}
